import jwtDecode from 'jwt-decode';
import {
  get, filter, find, minBy
} from 'lodash';
import { datadogRum } from '@datadog/browser-rum';
import { roles, getRoleName } from '../utils/roles';

export default {
  namespaced: true,
  state: {
    _id: undefined,
    authenticated: false,
    profile: {
      roles: [], //  { _id, national_id, type, entity: { _id, name } }
    },
    tokens: {}, // { access_token, refresh_token, id_token, expires_in}
    activeRole: {
      _id: undefined,
      clearanceAllowed: false,
      national_id: undefined,
      type: undefined,
      entity: {
        _id: undefined,
        name: undefined
      }
    },
    preference: {
      draw: {
        venuepriority: [0, 1, 2, 3, 4, 5]
      },
      indvdraw: {
        venuepriority: [0, 1]
      }
    },
    module: {

    }
  },
  mutations: {
    login(state, { profile, tokens }) {
      state.profile = profile;
      state.tokens = tokens;
      state.authenticated = true;
      state.profile.roles = filter(profile.roles, (x) => x.type === roles.assembleAdmin || x.national_id === +process.env.VUE_APP_NATIONAL_ID);

      // Load active role from local storage
      let activeRoleFromLocalStorage = localStorage.getItem('activeRole');
      if (activeRoleFromLocalStorage && activeRoleFromLocalStorage !== 'undefined') {
        activeRoleFromLocalStorage = JSON.parse(activeRoleFromLocalStorage);
        state.activeRole = activeRoleFromLocalStorage;
      }

      // Keep current active role when auth occurs (refresh token used in router guard and axios utils)
      const foundItem = find(state.profile.roles, { _id: get(state.activeRole, '_id', undefined), type: get(state.activeRole, 'type', undefined) });
      if (!state.activeRole || (!state.activeRole._id && !foundItem)) {
        state.activeRole = minBy(state.profile.roles, 'type');
      }
      // Save active role to local storage
      localStorage.setItem('activeRole', JSON.stringify(state.activeRole));

      datadogRum.setUser({
        id: state._id || state.profile?._id,
        name: `${state.profile.firstName} ${state.profile.lastName}`,
        email: state.profile.email,
        role: getRoleName(get(state, 'activeRole.type')),
        entity: get(state, 'activeRole.entity.name'),
        entityId: get(state, 'activeRole.entity._id'),
      });
    },
    logout(state) {
      state.authenticated = false;
      state.profile = {};
      state.tokens = {};
      state.activeRole = {
        _id: undefined,
        national_id: undefined,
        type: undefined,
        entity: {
          _id: undefined,
          name: undefined
        }
      };
    },
    verified(state) {
      state.profile.verified = true;
    },
    activeRole(state, _id) {
      state.activeRole = find(state.profile.roles, { _id });
      console.log('activeRole', state.activeRole);
      if (state.activeRole) {
        localStorage.setItem('activeRole', JSON.stringify(state.activeRole));
      }
    },
    removeRole(state, _id) {
      state.profile.roles = filter(state.profile.roles, (x) => x._id !== _id);
      state.activeRole = minBy(state.profile.roles, 'type');
    },
    updateRoles(state, payload) {
      console.log('updateRoles', payload);
      state.profile.roles = payload;
    },
    updateRole(state, role) {
      const findIndex = state.profile.roles.findIndex((x) => x._id === role._id);
      if (findIndex !== -1) {
        state.profile.roles[findIndex] = role;
      }
    },
    updateModule(state, payload) {
      state.module = payload;
    },
    updateDrawVenuePriority(state, payload) {
      state.preference.draw.venuepriority = payload;
    },
    updateIndvDrawVenuePriority(state, payload) {
      state.preference.indvdraw.venuepriority = payload;
    },
  },
  actions: {
    async updateRole({ commit }, roleId) {
      commit('activeRole', roleId);
      const res = await this._vm.$http.get('assemble/api/v1/admin/users/modules');
      const { module } = res.data.data;
      if (module) commit('updateModule', module);
    },
    async login({ commit }, tokens) {
      const profile = jwtDecode(tokens.id_token);
      commit('login', { profile, tokens });
    },
    async fetchModule({ commit }) {
      const res = await this._vm.$http.get('assemble/api/v1/admin/users/modules');
      const { module } = res.data.data;
      if (module) commit('updateModule', module);
    },
    async savePreference({ commit }, preference) {
      const { venuepriority, indvdrawvenue } = preference;
      if (venuepriority) commit('updateDrawVenuePriority', venuepriority);
      if (indvdrawvenue) commit('updateIndvDrawVenuePriority', indvdrawvenue);
    }
  },
  getters: {
    userId(state) {
      return get(state, 'profile._id');
    },
    displayName(state) {
      return `${get(state, 'profile.firstName')} ${get(state, 'profile.lastName')}`;
    },
    abbrDisplayName(state) {
      return `${get(state, 'profile.firstName[0]')}${get(state, 'profile.lastName[0]')}`;
    },
    entityOptions(state) {
      return state.profile.roles
        .filter((role) => role.entity)
        .sort((a, b) => a.type - b.type)
        .map((role) => ({
          _id: role.entity._id,
          name: role.entity.name,
          type: getRoleName(role.type),
          roleId: role._id,
        }));
    },
    accessToken(state) {
      return state.tokens.access_token;
    },
    refreshToken(state) {
      return state.tokens.refresh_token;
    },
    expiry(state) {
      return state.tokens.expires_in;
    },
    isAuthenticated(state) {
      return state.authenticated;
    },
    isVerified(state) {
      return state.profile.verified;
    },
    roles(state) {
      return state.profile.roles;
    },
    activeRole(state) {
      return state.activeRole;
    },
    activeRoleId(state) {
      return get(state, 'activeRole._id');
    },
    activeRoleEntityName(state) {
      const foundActiveRole = find(state.profile.roles, { _id: get(state.activeRole, '_id', undefined), type: get(state.activeRole, 'type', undefined) });
      return get(foundActiveRole, 'entity.name');
    },
    activeRoleType(state) {
      return get(state, 'activeRole.type') || 999;
    },
    // payload: {module-name}
    module(state) {
      return get(state, 'module');
    },
    drawVenuePriority(state) {
      return get(state, 'preference.draw.venuepriority');
    },
    indvDrawVenuePriority(state) {
      return get(state, 'preference.indvdraw.venuepriority');
    },
    uploadHeaders(state) {
      const { profile, tokens, activeRole } = state;
      const authheader = `${profile._id}:${tokens.access_token}`;
      const encodedString = btoa(authheader);
      const Authorization = `Bearer ${encodedString}`;
      return { Authorization, 'active-role': activeRole._id, 'national-id': activeRole.national_id };
    },
    entityLevels(state) {
      switch (get(state, 'activeRole.type')) {
        case 2:
          return [
            { label: 'National', value: 'national' },
            { label: 'State', value: 'state' },
            { label: 'Association', value: 'association' },
            { label: 'Club', value: 'club' }
          ];
        case 3:
          return [
            { label: 'State', value: 'state' },
            { label: 'Association', value: 'association' },
            { label: 'Club', value: 'club' }
          ];
        case 4:
          return [
            { label: 'Association', value: 'association' },
            { label: 'Club', value: 'club' }
          ];
        case 5:
          return [
            { label: 'Club', value: 'club' }
          ];
        default: break;
      }
      return [
        { label: 'National', value: 'national' },
        { label: 'State', value: 'state' },
        { label: 'Association', value: 'association' },
        { label: 'Club', value: 'club' }
      ];
    },
  },
};
